// Pure decision logic for the transaction-match footer's confirm button.
// Extracted so the label and the "already persisted" guard can be unit-tested
// without rendering the whole drawer.

// Local selection id for the cash-basis transfer partner already persisted on
// the source transaction. Only cash-basis pairs are persisted as a
// pairedTransferId; the accrual path writes an AccrualMatch row instead and has
// its own already-approved handling.
export function getConfirmedPairItemId({
    isCashBasis,
    pairedTransferId,
}: {
    isCashBasis: boolean;
    pairedTransferId: number | null | undefined;
}): string | null {
    return isCashBasis && pairedTransferId != null
        ? `tx-${pairedTransferId}`
        : null;
}

// True when the only selected item is the already-persisted transfer partner,
// so confirming again would be a no-op. Guards the footer from toasting
// "Match saved" for a pair that is already saved (PRO-10954).
export function getIsSelectionAlreadyPersisted({
    confirmedPairItemId,
    selectedItemIds,
}: {
    confirmedPairItemId: string | null;
    selectedItemIds: string[];
}): boolean {
    return (
        confirmedPairItemId !== null &&
        selectedItemIds.length === 1 &&
        selectedItemIds[0] === confirmedPairItemId
    );
}

// General case of the same guard above: true whenever the current selection
// is exactly the set already persisted (existing AccrualMatch documents plus
// any already-paired transfer partner), regardless of how many items are
// selected or whether the drawer is on the cash or accrual path. Prevents
// resubmitting an unchanged match — not just the single-transfer-pair case
// `getIsSelectionAlreadyPersisted` originally covered.
export function getIsSelectionUnchanged({
    selectedItemIds,
    persistedItemIds,
}: {
    selectedItemIds: string[];
    persistedItemIds: ReadonlySet<string>;
}): boolean {
    if (selectedItemIds.length !== persistedItemIds.size) {
        return false;
    }
    return selectedItemIds.every((id) => persistedItemIds.has(id));
}

function isDocumentMatchId(id: string): boolean {
    return id.startsWith("inv-") || id.startsWith("bill-");
}

function isTransactionMatchId(id: string): boolean {
    return id.startsWith("tx-");
}

function isAccrualMatchId(id: string): boolean {
    return isDocumentMatchId(id) || isTransactionMatchId(id);
}

function getIsPersistedRemoval({
    selectedItemIds,
    persistedItemIds,
    isRemovableId,
}: {
    selectedItemIds: string[];
    persistedItemIds: ReadonlySet<string>;
    isRemovableId: (id: string) => boolean;
}): boolean {
    if (persistedItemIds.size === 0) {
        return false;
    }
    if (selectedItemIds.some((id) => !persistedItemIds.has(id))) {
        return false;
    }

    const selectedIds = new Set(selectedItemIds);
    for (const id of persistedItemIds) {
        if (!selectedIds.has(id) && isRemovableId(id)) {
            return true;
        }
    }
    return false;
}

/**
 * True when the edit only drops already-saved invoices or bills.
 * Save must persist that unmatch even if the leftover selection no longer
 * covers the transaction. New items keep the normal amount check.
 */
export function getIsPersistedDocumentRemoval({
    selectedItemIds,
    persistedItemIds,
}: {
    selectedItemIds: string[];
    persistedItemIds: ReadonlySet<string>;
}): boolean {
    return getIsPersistedRemoval({
        selectedItemIds,
        persistedItemIds,
        isRemovableId: isDocumentMatchId,
    });
}

/**
 * Same as a document removal, and also a saved transaction. Used on the
 * accrual path and in edit allocation. Cash transfer pairs stay on
 * `getIsPersistedDocumentRemoval` so they unmatch through the transfer
 * endpoint instead.
 */
export function getIsPersistedMatchRemoval({
    selectedItemIds,
    persistedItemIds,
}: {
    selectedItemIds: string[];
    persistedItemIds: ReadonlySet<string>;
}): boolean {
    return getIsPersistedRemoval({
        selectedItemIds,
        persistedItemIds,
        isRemovableId: isAccrualMatchId,
    });
}

export function getIsPersistedTransactionRemoval({
    selectedItemIds,
    persistedItemIds,
}: {
    selectedItemIds: string[];
    persistedItemIds: ReadonlySet<string>;
}): boolean {
    return getIsPersistedRemoval({
        selectedItemIds,
        persistedItemIds,
        isRemovableId: isTransactionMatchId,
    });
}

export function getIsConfirmDisabled({
    isAmountMatched,
    isPersistedDocumentRemoval,
    isLoading,
    isSelectionAlreadyPersisted,
    isSelectionUnchanged,
    hasBlockedReason,
}: {
    isAmountMatched: boolean;
    isPersistedDocumentRemoval: boolean;
    isLoading: boolean;
    isSelectionAlreadyPersisted: boolean;
    isSelectionUnchanged: boolean;
    /**
     * True when the selection is unconfirmable beyond the amount check
     * itself — mixed doc types, an unsupported cross-entity doc, cash-basis
     * over-selection, or (PRO-12200) the source transaction/any selected
     * match sitting in a locked accounting period. Even a removal-only save
     * (`isPersistedDocumentRemoval`) still writes a journal entry reversal
     * for the transaction, so a locked party keeps it gated too.
     */
    hasBlockedReason: boolean;
}): boolean {
    if (
        isLoading ||
        isSelectionAlreadyPersisted ||
        isSelectionUnchanged ||
        hasBlockedReason
    ) {
        return true;
    }
    if (isPersistedDocumentRemoval) {
        return false;
    }
    return !isAmountMatched;
}

// Edit allocation stays optional (and available) for a transaction that is
// smaller than or equal to the selected invoices/bills — there's no leftover
// cash to book anywhere. Resolve difference is required only once the
// transaction is strictly bigger than the selection, since that leftover has
// to land somewhere before the match can be confirmed (PRO-12049).
export function getDifferenceActionLabel(difference: number): string {
    return difference > 0 ? "Resolve difference" : "Edit allocation";
}

export function getIsRemovalOnlyEdit({
    selectedItemIds,
    initialSelectedIds,
}: {
    selectedItemIds: readonly string[];
    initialSelectedIds: ReadonlySet<string>;
}): boolean {
    if (
        initialSelectedIds.size === 0 ||
        selectedItemIds.length >= initialSelectedIds.size
    ) {
        return false;
    }
    return selectedItemIds.every((id) => initialSelectedIds.has(id));
}

export function getIsSuggestedMatchRemoval({
    selectedItemIds,
    persistedItemIds,
    suggestedItemIds,
}: {
    selectedItemIds: readonly string[];
    persistedItemIds: ReadonlySet<string>;
    suggestedItemIds: readonly string[];
}): boolean {
    const removedSuggestion = suggestedItemIds.some(
        (id) => !selectedItemIds.includes(id),
    );
    if (!removedSuggestion) {
        return false;
    }
    return selectedItemIds.every((id) => persistedItemIds.has(id));
}

export function getIsUnapprovedDocumentSelection({
    selectedDocumentIds,
    suggestedItemIds,
    dismissedSuggestionIds,
}: {
    selectedDocumentIds: readonly string[];
    suggestedItemIds: readonly string[];
    dismissedSuggestionIds: ReadonlySet<string>;
}): boolean {
    const activeSuggestionIds = new Set(
        suggestedItemIds.filter((id) => !dismissedSuggestionIds.has(id)),
    );
    return selectedDocumentIds.some((id) => activeSuggestionIds.has(id));
}

export function getRemovalConfirmLabel(): string {
    return "Save changes";
}

export function getConfirmLabel({
    isCashBasis,
    isAlreadyApproved,
    isSelectionAlreadyPersisted,
    isDocumentApproval = false,
}: {
    isCashBasis: boolean;
    isAlreadyApproved: boolean;
    isSelectionAlreadyPersisted: boolean;
    isDocumentApproval?: boolean;
}): string {
    if (isCashBasis) {
        if (isSelectionAlreadyPersisted) {
            return "Matched";
        }
        return isDocumentApproval ? "Confirm match" : "Match";
    }

    return isAlreadyApproved ? "Save changes" : "Confirm match";
}
